import SkeletonComponent from '@atoms/SkeletonComponent';
import useFetchBanks from '@customHooks/useFetchBanks';
import ProfileDefaultUserBank from '@molecules/ProfileDefaultUserBank';
import ProfileViewRegisteredBankDetails from '@molecules/ProfileViewRegisteredBankDetails';
import { useIsFocused } from '@react-navigation/native';
import { AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
import React, { useEffect, useMemo } from 'react';

// Define interfaces
interface DefaultBank {
  bankName: string;
  bankAccountName: string;
  number: string;
}

interface ProfileBankDetailsProps {
  showBankDetails?: boolean;
  defaultBank?: DefaultBank;
}

const ProfileBankDetails: React.FC<ProfileBankDetailsProps> = ({
  showBankDetails = false,
  defaultBank = { bankName: '', bankAccountName: '', number: '' },
}) => {
  const safeDefaultBank = defaultBank ?? { bankName: '', bankAccountName: '', number: '' };

  const { bankName, bankAccountName, number } = safeDefaultBank;
  const isFocused = useIsFocused();

  // Fetch banks using custom hook
  const { isLoading, isSuccess, bankList, refetch } = useFetchBanks();

  // Refetch bank data when the component is focused
  useEffect(() => {
    if (isFocused) {
      refetch();
    }
  }, [isFocused, refetch]);

  // Memoize enrolled banks for performance
  const enrolledBankList = useMemo(() => (isSuccess ? bankList : []), [isSuccess, bankList]);

  // Conditional rendering for loading state
  if (isLoading) {
    return <SkeletonComponent />;
  }

  // Conditional rendering for bank details
  if (showBankDetails) {
    if (enrolledBankList?.length > 0) {
      return (
        <ProfileViewRegisteredBankDetails
          employeeDefaultBank={safeDefaultBank}
          source={AMPLITUDE_EVENT_SOURCES.others_page}
        />
      );
    }
    if (safeDefaultBank) {
      return <ProfileDefaultUserBank bankName={bankName} beneficiaryName={bankAccountName} bankAccountNo={number} />;
    }
    return <SkeletonComponent />;
  }

  return null;
};

export default React.memo(ProfileBankDetails);
